import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { Button, ThemedText, ErrorState } from '../components';
import aiUsageLogService from '../api/services/aiUsageLogService';
import { organizationService } from '../api';
import { extractItems } from '../utils/responseUtils';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { StatusPalette, withOpacity } from '../constants/statusColors';

// ─── Helpers ───────────────────────────────────────────────────────

const formatCost = (val) => {
  if (val == null) return '—';
  if (val === 0) return '$0.00';
  if (val < 0.01) return `$${Number(val).toFixed(6)}`;
  return `$${Number(val).toFixed(4)}`;
};

const formatTokens = (val) => {
  if (val == null || val === 0) return '0';
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
  return val.toLocaleString();
};

const formatLatency = (ms) => {
  if (!ms) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
};

// ─── Styled Components ────────────────────────────────────────────

const FilterBar = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.lg};
  flex-wrap: wrap;
  align-items: center;
`;

const FilterInput = styled.input`
  padding: 7px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  min-width: 140px;
`;

const FilterSelect = styled.select`
  padding: 7px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  min-width: 140px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${p => p.$accent ? p.theme.colors.tint : p.theme.colors.text};
  line-height: 1.1;
`;

const StatSub = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  margin-top: 3px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  margin: 0 0 ${p => p.theme.spacing.md} 0;
`;

const TableContainer = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  overflow: hidden;
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Thead = styled.thead``;

const Th = styled.th`
  padding: 10px 14px;
  text-align: ${p => p.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  border-bottom: 2px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${p => p.theme.colors.border}40;
  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.theme.colors.background}; }
`;

const Td = styled.td`
  padding: 10px 14px;
  text-align: ${p => p.$right ? 'right' : 'left'};
  color: ${p => p.$muted ? p.theme.colors.muted : p.theme.colors.text};
  font-size: 12px;
  vertical-align: middle;
  white-space: ${p => p.$nowrap ? 'nowrap' : 'normal'};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => p.$status === 'Success' ? withOpacity(StatusPalette.green) : withOpacity(StatusPalette.red)};
  color: ${p => p.$status === 'Success' ? StatusPalette.green : StatusPalette.red};
`;

const Monospace = styled.span`
  font-family: monospace;
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const PaginationButton = styled.button`
  padding: 5px 12px;
  font-size: 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.disabled ? p.theme.colors.backgroundSecondary : p.theme.colors.background};
  color: ${p => p.disabled ? p.theme.colors.muted : p.theme.colors.text};
  cursor: ${p => p.disabled ? 'default' : 'pointer'};
  &:hover:not(:disabled) { background: ${p => p.theme.colors.backgroundSecondary}; }
`;

const EmptyState = styled.div`
  padding: ${p => p.theme.spacing.xl};
  text-align: center;
  color: ${p => p.theme.colors.muted};
  font-size: 14px;
`;

const CostValue = styled.span`
  font-weight: 600;
  color: ${p => p.theme.colors.tint};
`;

// ─── Component ────────────────────────────────────────────────────

export const AIUsageSection = () => {
  const theme = useTheme();
  const { isSuperAdmin } = useRole();
  const isSuper = isSuperAdmin();

  // Date range defaults to this month
  const now = new Date();
  const defaultFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const defaultTo = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

  // Filters
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [purposeFilter, setPurposeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Data
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  // Load organizations list (SuperAdmin only)
  useEffect(() => {
    if (!isSuper) return;
    const fetchOrgs = async () => {
      try {
        const res = await organizationService.getOrganizations({ pageSize: 200 });
        setOrganizations(extractItems(res) || []);
      } catch {
        // non-critical
      }
    };
    fetchOrgs();
  }, [isSuper]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      setFetchError(null);
      const params = {};
      if (fromDate) params.from = new Date(fromDate).toISOString();
      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        params.to = to.toISOString();
      }
      if (selectedOrgId) params.organizationId = selectedOrgId;
      const res = await aiUsageLogService.getSummary(params);
      setSummary(res);
    } catch (error) {
      setFetchError(error?.response?.data?.message || 'Failed to load AI usage data. Please check your connection and try again.');
      toast.error('Failed to fetch AI usage summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [fromDate, toDate, selectedOrgId]);

  const fetchLogs = useCallback(async (pg) => {
    setLogsLoading(true);
    try {
      const params = { page: pg ?? page, pageSize };
      if (fromDate) params.from = new Date(fromDate).toISOString();
      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        params.to = to.toISOString();
      }
      if (selectedOrgId) params.organizationId = selectedOrgId;
      if (serviceTypeFilter) params.serviceType = serviceTypeFilter;
      if (purposeFilter) params.purpose = purposeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await aiUsageLogService.getLogs(params);
      setLogs(res);
    } catch {
      toast.error('Failed to fetch AI usage logs');
    } finally {
      setLogsLoading(false);
    }
  }, [fromDate, toDate, selectedOrgId, serviceTypeFilter, purposeFilter, statusFilter, page, pageSize]);

  // Initial load — re-run when top-level date/org filters change
  // (log-level filters applied via Apply button)
  useEffect(() => {
    fetchSummary();
    fetchLogs(1);
    setPage(1);
    // fetchSummary and fetchLogs change when the filter state changes,
    // so listing them would cause extra loops. We intentionally use date/org only.
    // eslint-disable-next-line
  }, [fromDate, toDate, selectedOrgId]);

  const handleApplyFilters = () => {
    setPage(1);
    fetchSummary();
    fetchLogs(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchLogs(newPage);
  };

  const successRate = summary?.totalCalls > 0
    ? Math.round((summary.successfulCalls / summary.totalCalls) * 100)
    : null;

  return (
      <PageContainer>
        <PageHeader>
          <ThemedText type="title" style={{ color: theme.colors.text }}>AI Usage</ThemedText>
          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            <Button variant="secondary" size="sm" onClick={handleApplyFilters} disabled={summaryLoading || logsLoading}>
              {summaryLoading || logsLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
        </PageHeader>

        {/* Filter Bar */}
        <FilterBar theme={theme}>
          <FilterInput
            theme={theme}
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            title="From date"
          />
          <FilterInput
            theme={theme}
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            title="To date"
          />
          {isSuper && (
            <FilterSelect
              theme={theme}
              value={selectedOrgId}
              onChange={e => setSelectedOrgId(e.target.value)}
            >
              <option value="">All Organizations</option>
              {organizations.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </FilterSelect>
          )}
        </FilterBar>

        {fetchError ? (
          <ErrorState
            theme={theme}
            title="Failed to load AI usage"
            message={fetchError}
            onRetry={handleApplyFilters}
          />
        ) : (<>
        {/* Summary Cards */}
        <SummaryGrid theme={theme}>
          <StatCard theme={theme}>
            <StatLabel theme={theme}>Total Calls</StatLabel>
            <StatValue theme={theme}>{summary?.totalCalls?.toLocaleString() ?? (summaryLoading ? '...' : '0')}</StatValue>
            <StatSub theme={theme}>
              {summary ? `${summary.successfulCalls} success, ${summary.failedCalls} failed` : '\u00A0'}
            </StatSub>
          </StatCard>

          <StatCard theme={theme}>
            <StatLabel theme={theme}>Success Rate</StatLabel>
            <StatValue theme={theme}>{successRate != null ? `${successRate}%` : (summaryLoading ? '...' : '—')}</StatValue>
            <StatSub theme={theme}>
              {summary?.failedCalls > 0 ? `${summary.failedCalls} failed calls` : 'No failures'}
            </StatSub>
          </StatCard>

          <StatCard theme={theme}>
            <StatLabel theme={theme}>Total Tokens</StatLabel>
            <StatValue theme={theme}>{summary ? formatTokens(summary.totalTokens) : (summaryLoading ? '...' : '0')}</StatValue>
            <StatSub theme={theme}>
              {summary ? `${formatTokens(summary.totalInputTokens)} in / ${formatTokens(summary.totalOutputTokens)} out` : '\u00A0'}
            </StatSub>
          </StatCard>

          <StatCard theme={theme}>
            <StatLabel theme={theme}>Avg Latency</StatLabel>
            <StatValue theme={theme}>{summary ? formatLatency(summary.averageLatencyMs) : (summaryLoading ? '...' : '—')}</StatValue>
            <StatSub theme={theme}>{summary ? `${summary.totalPagesProcessed} pages (Textract)` : '\u00A0'}</StatSub>
          </StatCard>

          <StatCard theme={theme}>
            <StatLabel theme={theme}>Est. Cost</StatLabel>
            <StatValue theme={theme} $accent>
              {summary ? formatCost(summary.totalEstimatedCostUsd) : (summaryLoading ? '...' : '$0.00')}
            </StatValue>
            <StatSub theme={theme}>AWS on-demand pricing</StatSub>
          </StatCard>
        </SummaryGrid>

        {/* By Purpose breakdown */}
        {summary?.byPurpose?.length > 0 && (
          <>
            <SectionTitle theme={theme}>By Purpose</SectionTitle>
            <TableContainer theme={theme}>
              <Table>
                <Thead>
                  <tr>
                    <Th theme={theme}>Purpose</Th>
                    <Th theme={theme}>Service</Th>
                    <Th theme={theme} $right>Calls</Th>
                    <Th theme={theme} $right>Tokens</Th>
                    <Th theme={theme} $right>Pages</Th>
                    <Th theme={theme} $right>Avg Latency</Th>
                    <Th theme={theme} $right>Est. Cost</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {summary.byPurpose.map((p, i) => (
                    <Tr key={i} theme={theme}>
                      <Td theme={theme}>{p.purpose}</Td>
                      <Td theme={theme} $muted>{p.serviceType}</Td>
                      <Td theme={theme} $right>{p.callCount.toLocaleString()}</Td>
                      <Td theme={theme} $right $muted>{p.totalTokens > 0 ? formatTokens(p.totalTokens) : '—'}</Td>
                      <Td theme={theme} $right $muted>{p.totalPages > 0 ? p.totalPages : '—'}</Td>
                      <Td theme={theme} $right $muted>{formatLatency(p.averageLatencyMs)}</Td>
                      <Td theme={theme} $right>
                        <CostValue theme={theme}>{formatCost(p.estimatedCostUsd)}</CostValue>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* By Organization (SuperAdmin, no org filter) */}
        {isSuper && !selectedOrgId && summary?.byOrganization?.length > 0 && (
          <>
            <SectionTitle theme={theme}>By Organization</SectionTitle>
            <TableContainer theme={theme}>
              <Table>
                <Thead>
                  <tr>
                    <Th theme={theme}>Organization</Th>
                    <Th theme={theme} $right>LLM Calls</Th>
                    <Th theme={theme} $right>Textract Calls</Th>
                    <Th theme={theme} $right>Tokens</Th>
                    <Th theme={theme} $right>Est. Cost</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {summary.byOrganization.map((o, i) => (
                    <Tr key={i} theme={theme}>
                      <Td theme={theme}>
                        {o.organizationName || (o.organizationId ? `Org #${o.organizationId}` : 'System / No Org')}
                      </Td>
                      <Td theme={theme} $right>{o.llmCalls.toLocaleString()}</Td>
                      <Td theme={theme} $right>{o.textractCalls.toLocaleString()}</Td>
                      <Td theme={theme} $right $muted>{formatTokens(o.totalTokens)}</Td>
                      <Td theme={theme} $right>
                        <CostValue theme={theme}>{formatCost(o.totalEstimatedCostUsd)}</CostValue>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Call Log */}
        <SectionTitle theme={theme}>Call Log</SectionTitle>

        {/* Log filters */}
        <FilterBar theme={theme} style={{ marginBottom: theme.spacing.md }}>
          <FilterSelect
            theme={theme}
            value={serviceTypeFilter}
            onChange={e => setServiceTypeFilter(e.target.value)}
          >
            <option value="">All Services</option>
            <option value="LLM">LLM</option>
            <option value="Textract">Textract</option>
          </FilterSelect>
          <FilterSelect
            theme={theme}
            value={purposeFilter}
            onChange={e => setPurposeFilter(e.target.value)}
          >
            <option value="">All Purposes</option>
            <option value="FormSchema">FormSchema</option>
            <option value="PdfTemplate">PdfTemplate</option>
            <option value="LabReport">LabReport</option>
            <option value="EmailTemplate">EmailTemplate</option>
            <option value="SmsTemplate">SmsTemplate</option>
            <option value="Tier.Fast">Tier.Fast</option>
            <option value="Tier.Balanced">Tier.Balanced</option>
            <option value="Tier.Powerful">Tier.Powerful</option>
            <option value="Textract">Textract</option>
          </FilterSelect>
          <FilterSelect
            theme={theme}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Success">Success</option>
            <option value="Failed">Failed</option>
          </FilterSelect>
          <Button variant="secondary" size="sm" onClick={handleApplyFilters}>Apply</Button>
        </FilterBar>

        <TableContainer theme={theme}>
          {logsLoading ? (
            <EmptyState theme={theme}>Loading call log...</EmptyState>
          ) : logs?.items?.length > 0 ? (
            <>
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <Thead>
                    <tr>
                      <Th theme={theme}>Time</Th>
                      <Th theme={theme}>Service</Th>
                      <Th theme={theme}>Purpose</Th>
                      <Th theme={theme}>Model</Th>
                      <Th theme={theme} $right>Tokens / Pages</Th>
                      <Th theme={theme} $right>Latency</Th>
                      <Th theme={theme}>Status</Th>
                      <Th theme={theme} $right>Est. Cost</Th>
                      {isSuper && <Th theme={theme}>Org</Th>}
                      <Th theme={theme}>User</Th>
                    </tr>
                  </Thead>
                  <Tbody>
                    {logs.items.map(log => (
                      <Tr key={log.id} theme={theme}>
                        <Td theme={theme} $muted $nowrap>{formatDateTime(log.createdAt)}</Td>
                        <Td theme={theme}>{log.serviceType}</Td>
                        <Td theme={theme}>{log.purpose}</Td>
                        <Td theme={theme}>
                          <Monospace theme={theme} title={log.modelId}>
                            {log.modelId ? log.modelId.split('.').slice(-2).join('.') : '—'}
                          </Monospace>
                        </Td>
                        <Td theme={theme} $right $muted>
                          {log.totalTokens != null
                            ? formatTokens(log.totalTokens)
                            : log.pagesProcessed != null
                              ? `${log.pagesProcessed} pg`
                              : '—'}
                        </Td>
                        <Td theme={theme} $right $muted>{formatLatency(log.latencyMs)}</Td>
                        <Td theme={theme}>
                          <StatusBadge $status={log.status}>{log.status}</StatusBadge>
                        </Td>
                        <Td theme={theme} $right>
                          {log.estimatedCostUsd != null
                            ? <CostValue theme={theme}>{formatCost(log.estimatedCostUsd)}</CostValue>
                            : <span style={{ color: theme.colors.muted }}>—</span>}
                        </Td>
                        {isSuper && (
                          <Td theme={theme} $muted>
                            {log.organizationName || (log.organizationId ? `#${log.organizationId}` : '—')}
                          </Td>
                        )}
                        <Td theme={theme} $muted>
                          <Monospace theme={theme}>{log.userEmail || '—'}</Monospace>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </div>
              <PaginationRow theme={theme}>
                <span>{logs.totalCount?.toLocaleString()} total entries</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <PaginationButton
                    theme={theme}
                    disabled={page <= 1}
                    onClick={() => handlePageChange(page - 1)}
                  >Previous</PaginationButton>
                  <span>Page {page} of {logs.totalPages || 1}</span>
                  <PaginationButton
                    theme={theme}
                    disabled={page >= (logs.totalPages || 1)}
                    onClick={() => handlePageChange(page + 1)}
                  >Next</PaginationButton>
                </div>
              </PaginationRow>
            </>
          ) : (
            <EmptyState theme={theme}>No AI usage calls found for the selected period.</EmptyState>
          )}
        </TableContainer>
        </>)}
      </PageContainer>
  );
};

export const AIUsagePage = () => (
  <Layout currentPath="/settings">
    <AIUsageSection />
  </Layout>
);

export default AIUsagePage;
